import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { QrCode, RefreshCw, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const DOC_TYPES = [
  { value: 'rcds', label: 'RCDS BATICO' },
  { value: 'recu-ess', label: 'Recu ESS' },
  { value: 'attestation', label: 'Attestation RCDS' },
  { value: 'avenant', label: 'Avenant RCDS' },
  { value: 'ftpe', label: 'FTPE Adhesion' },
  { value: 'recu-ftpe', label: 'Recu FTPE' },
  { value: 'cp', label: 'Conditions Particulieres' },
  { value: 'proposition', label: 'Proposition Commerciale' },
  { value: 'autre', label: 'Autre' }
];

export const docTypeLabel = (val) => DOC_TYPES.find(d => d.value === val)?.label || val;

// Recherche multi-type (souscription + GED + facture + adhésion FTPE) — V36
export const TYPE_LABELS = {
  souscription: { label: 'Souscription', color: 'bg-indigo-100 text-indigo-700', icon: '📄' },
  ged: { label: 'GED', color: 'bg-amber-100 text-amber-800', icon: '📁' },
  facture: { label: 'Facture', color: 'bg-emerald-100 text-emerald-800', icon: '💰' },
  ftpe_adhesion: { label: 'Adhésion FTPE', color: 'bg-violet-100 text-violet-800', icon: '🎫' },
};

export function TargetSearch({ onSelect, headers, selectedTypes }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const types = (selectedTypes && selectedTypes.length > 0)
        ? selectedTypes.join(',')
        : 'souscription,ged,facture,ftpe_adhesion';
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/qr-codes/search-targets?q=${encodeURIComponent(q)}&types=${encodeURIComponent(types)}`,
        { headers }
      );
      setResults(res.data.results || []);
      setOpen(true);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, [headers, selectedTypes]);

  const handleChange = (val) => {
    setQuery(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleSelect = (target) => {
    onSelect(target);
    setQuery(`${target.label_main}${target.label_sub ? ' — ' + target.label_sub : ''}`);
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Cible (souscription, GED, facture, adhésion FTPE) *
      </label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={e => handleChange(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Rechercher par référence, raison sociale, nom de document..."
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
          data-testid="qr-target-search"
        />
        {loading && <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />}
      </div>
      <p className="text-xs text-gray-500 mt-1">
        Recherche dans <strong>toute la plateforme</strong> (souscriptions BATICO, documents GED, factures, adhésions FTPE).
      </p>

      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto" data-testid="qr-target-dropdown">
          {results.map(target => {
            const meta = TYPE_LABELS[target.target_type] || TYPE_LABELS.souscription;
            return (
              <button
                key={`${target.target_type}-${target.target_id}`}
                type="button"
                onClick={() => handleSelect(target)}
                className="w-full text-left px-4 py-2.5 hover:bg-indigo-50 border-b border-gray-100 last:border-0 transition-colors"
                data-testid={`qr-target-option-${target.target_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge className={`${meta.color} text-[10px] font-semibold uppercase tracking-wider flex-shrink-0`}>
                      {meta.icon} {meta.label}
                    </Badge>
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 text-sm truncate">{target.label_main}</div>
                      {target.label_sub && (
                        <div className="text-gray-500 text-xs truncate">{target.label_sub}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {target.statut && <Badge variant="outline" className="text-xs">{target.statut}</Badge>}
                    {target.has_qr && (
                      <Badge className="bg-blue-700 text-white text-xs font-semibold">
                        <QrCode className="h-3 w-3 mr-1" />QR
                      </Badge>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {open && query.length >= 2 && results.length === 0 && !loading && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-sm text-gray-500">
          Aucune cible trouvée. Vérifiez l&apos;orthographe ou élargissez le filtre par type.
        </div>
      )}
    </div>
  );
}

// Alias rétrocompat (le composant interne est désormais TargetSearch)
export const SouscriptionSearch = TargetSearch;
